import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { SelectDestinationFolderModal } from "@/components/features/matter/file-repo/signature-requests/SelectDestinationFolderModal";
import {
  MassTortMatterSelectionModal,
  SelectedMattersAccordion,
} from "@/components/features/mass-torts/MassTortMatterSelectionModal";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  FormSubmitButton,
  Input,
  MultiselectFormField,
  SearchableSelectFormField,
  Separator,
  SimpleSelectFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { retrieveMatterTemplatesQueryOptions } from "@/lib/data/react-query/matters/matter-template";
import {
  useGenerateMassTortMatterTemplateDocuments,
  useRetryFailedMassTortTemplateGenerationJobs,
} from "@/lib/data/react-query/mass-torts/mass-tort";
import {
  buildInstanceGroups,
  buildTemplateVariableSections,
  getGroupHeadingSuffix,
  getRoleLabelForKey,
} from "@/components/features/mass-torts/massTortFileGenerationTemplateVariables";
import type {
  MassTort,
  MassTortCustomField,
  MassTortGeneratedFileTemplateRequest,
  MatterTemplate,
  ReportsMatterSearchTableRow,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { z } from "zod";

const GENERATE_MASS_TORT_FILES_FORM_ID = "generate-mass-tort-files-form";

const formSchema = z.object({
  id: z.string().min(1, {
    message: "Please select a template",
  }),
  generatedFileName: z.string().trim().min(1, {
    message: "Please enter a file name",
  }),
  fileType: z
    .array(z.enum([".pdf", ".docx", ".doc"]))
    .refine((value) => value.length > 0, {
      message: "Must select at least one file type",
    }),
  matterTemplateInputs: z.record(z.string().optional()).optional(),
});

type GenerateMassTortFilesFormData = z.infer<typeof formSchema>;

const defaultValues: GenerateMassTortFilesFormData = {
  id: "",
  generatedFileName: "",
  fileType: [".pdf"],
  matterTemplateInputs: {},
};

const fileTypeOptions = [
  {
    value: ".pdf",
    label: "PDF",
  },
  {
    value: ".docx",
    label: "DOCX",
  },
  {
    value: ".doc",
    label: "DOC",
  },
] as const;

type MatterFileExtension = (typeof fileTypeOptions)[number]["value"];

function normalizeMatterFileExtension(
  extension?: string | null,
): MatterFileExtension {
  const normalizedExtension = extension?.trim().toLowerCase();

  if (fileTypeOptions.some((option) => option.value === normalizedExtension)) {
    return normalizedExtension as MatterFileExtension;
  }

  return ".pdf";
}

function getFileTypeLabel(extension?: string) {
  return (
    fileTypeOptions.find((option) => option.value === extension)?.label ??
    extension ??
    ""
  );
}

type GenerateMassTortFilesModalBaseProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  massTortCustomFields: MassTortCustomField[];
};

type GenerateMassTortFilesModalProps = GenerateMassTortFilesModalBaseProps &
  (
    | {
        mode: "generate";
        massTortId: string;
        massTort: MassTort;
      }
    | {
        mode: "retry";
        request: MassTortGeneratedFileTemplateRequest;
        matterId?: string;
        matterTitle?: string;
      }
  );

const getRetryDefaultValues = (
  request: MassTortGeneratedFileTemplateRequest,
): GenerateMassTortFilesFormData => ({
  id: request.templateId ?? "",
  generatedFileName: request.name,
  fileType: [normalizeMatterFileExtension(request.extension)],
  matterTemplateInputs: {},
});

export function GenerateMassTortFilesModal(
  props: GenerateMassTortFilesModalProps,
) {
  const { open, setOpen } = props;
  const isRetryMode = props.mode === "retry";
  const generationMassTort = props.mode === "generate" ? props.massTort : null;
  const currentDefaultValues = useMemo(
    () => (isRetryMode ? getRetryDefaultValues(props.request) : defaultValues),
    [isRetryMode, props],
  );
  const form = useForm<GenerateMassTortFilesFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: currentDefaultValues,
    shouldUnregister: true,
  });
  const [isMatterPickerOpen, setIsMatterPickerOpen] = useState(false);
  const [selectedMatters, setSelectedMatters] = useState<
    ReportsMatterSearchTableRow[]
  >([]);
  const [isSelectDestinationFolderOpen, setIsSelectDestinationFolderOpen] =
    useState(false);
  const [selectedDestinationFolderId, setSelectedDestinationFolderId] =
    useState("");
  const generateMassTortFiles = useGenerateMassTortMatterTemplateDocuments();
  const retryFailedJobs = useRetryFailedMassTortTemplateGenerationJobs();
  const {
    data: matterTemplatesResponse,
    isError: isMatterTemplatesError,
    isLoading: isMatterTemplatesLoading,
  } = useQuery(retrieveMatterTemplatesQueryOptions());
  const matterTemplates = useMemo(
    () => matterTemplatesResponse?.data ?? [],
    [matterTemplatesResponse?.data],
  );
  const selectedTemplateId = form.watch("id");
  const selectedTemplate = useMemo(
    () =>
      matterTemplates.find((template) => template.id === selectedTemplateId),
    [matterTemplates, selectedTemplateId],
  );
  const templateFileNameParts = getFileNameParts(selectedTemplate?.name);
  const templateFileName = templateFileNameParts.fileExtension
    ? selectedTemplate?.name
    : selectedTemplate?.presignedUrlKey;
  const templateFile = useMemo(
    () => ({
      fileName: templateFileName ?? "",
      presignedURLKey: selectedTemplate?.presignedUrlKey || "",
    }),
    [templateFileName, selectedTemplate?.presignedUrlKey],
  );
  const templateVariables = useMemo(
    () =>
      (selectedTemplate?.variables ?? []).filter(
        (variable) =>
          !variable.startsWith("Signer") && !variable.startsWith("UserSigner"),
      ),
    [selectedTemplate?.variables],
  );
  const templateVariableSections = useMemo(
    () =>
      buildTemplateVariableSections(
        templateVariables,
        props.massTortCustomFields,
      ),
    [props.massTortCustomFields, templateVariables],
  );
  const generationMassTortFiles = useMemo(
    () => generationMassTort?.files ?? [],
    [generationMassTort?.files],
  );
  const selectedDestinationFolder = useMemo(
    () =>
      generationMassTortFiles.find(
        (file) => file.id === selectedDestinationFolderId && file.isFolder,
      ) ?? null,
    [generationMassTortFiles, selectedDestinationFolderId],
  );
  const selectedDestinationFolderParentPath = useMemo(() => {
    if (!selectedDestinationFolder) {
      return "";
    }

    const segments: string[] = ["Mass Tort Files"];
    let parentId = selectedDestinationFolder.idLocation;

    while (parentId) {
      const parentFolder = generationMassTortFiles.find(
        (file) => file.id === parentId && file.isFolder,
      );
      if (!parentFolder) {
        break;
      }
      segments.push(parentFolder.name);
      parentId = parentFolder.idLocation;
    }

    return segments.join(" / ");
  }, [generationMassTortFiles, selectedDestinationFolder]);
  const destinationFolderLabel = useMemo(() => {
    if (!selectedDestinationFolder) {
      return "Mass Tort Files (root)";
    }

    return selectedDestinationFolderParentPath
      ? `${selectedDestinationFolderParentPath} / ${selectedDestinationFolder.name}`
      : selectedDestinationFolder.name;
  }, [selectedDestinationFolder, selectedDestinationFolderParentPath]);

  useEffect(() => {
    if (open) {
      form.reset(currentDefaultValues);
      setSelectedMatters([]);
      setIsMatterPickerOpen(false);
      setSelectedDestinationFolderId("");
      setIsSelectDestinationFolderOpen(false);
    }
  }, [currentDefaultValues, form, open]);

  const handleSubmit = async (data: GenerateMassTortFilesFormData) => {
    const templateInputs = Object.fromEntries(
      Object.entries(data.matterTemplateInputs ?? {})
        .map(([key, value]) => [key, value?.trim() ?? ""])
        .filter(([, value]) => Boolean(value)),
    );
    const selectedMatterIds = selectedMatters
      .map((matter) => matter.matterId)
      .filter(Boolean);

    if (isRetryMode) {
      await retryFailedJobs.mutateAsync({
        templateInputs,
        requests: [
          {
            massTortId: props.request.massTortId,
            requestId: props.request.id,
            ...(props.matterId ? { matterIds: [props.matterId] } : {}),
          },
        ],
      });
    } else {
      await Promise.all(
        data.fileType.map((fileType) =>
          generateMassTortFiles.mutateAsync({
            extension: fileType,
            massTortId: props.massTortId,
            idLocation: selectedDestinationFolderId,
            name: data.generatedFileName.trim(),
            templateId: data.id,
            templateInputs,
            ...(selectedMatterIds.length
              ? { matterIds: selectedMatterIds }
              : {}),
          }),
        ),
      );
    }

    form.reset(currentDefaultValues);
    setOpen(false);
  };

  const handleInvalidSubmit = (
    errors: FieldErrors<GenerateMassTortFilesFormData>,
  ) => {
    console.error("[GenerateMassTortFilesModal] Zod validation failed", {
      errors,
      values: form.getValues(),
    });
  };

  const isSubmitting =
    generateMassTortFiles.isPending || retryFailedJobs.isPending;
  const title = isRetryMode
    ? props.matterTitle
      ? `Retry ${props.matterTitle}`
      : "Retry failed jobs"
    : "Generate files";
  const description = isRetryMode
    ? "Update optional template inputs before retrying failed file generation jobs."
    : "Generate files for the matters connected to this mass tort.";
  const submitTitle = isRetryMode ? "Retry" : "Generate files";
  const templateInputHelperText = isRetryMode
    ? "Template input values are optional. Leave a field blank to let each matter provide its own value during retry."
    : "Template input values are optional. Leave a field blank to let each matter provide its own value during generation.";
  const selectedMatterCount = selectedMatters.length;

  return (
    <Form {...form}>
      <>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          isDirty={form.formState.isDirty}
          loading={isSubmitting}
          size="3xl"
          bodyContained
          footer={
            <div className="flex w-full justify-end gap-2">
              <FormCancelButton
                setOpen={setOpen}
                isDirty={form.formState.isDirty}
              />
              <FormSubmitButton
                title={submitTitle}
                submitFormId={GENERATE_MASS_TORT_FILES_FORM_ID}
                loading={isSubmitting}
                overrideDisabled
              />
            </div>
          }
        >
          <div className="flex grow flex-col overflow-hidden">
            <form
              id={GENERATE_MASS_TORT_FILES_FORM_ID}
              className="flex grow flex-col overflow-hidden"
              onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}
            >
              <div className="overflow-y-auto">
                <fieldset
                  className={cn(
                    slideoverFieldsetClassName,
                    "grid-cols-1 md:grid-cols-2",
                  )}
                  disabled={isSubmitting}
                >
                  <SearchableSelectFormField<MatterTemplate>
                    items={matterTemplates}
                    register={form.register("id")}
                    label="Select template"
                    itemsKey="id"
                    buttonClassName="min-w-[200px]"
                    formatItemLabel={(item) =>
                      getFileNameParts(item?.name).fileName
                    }
                    formatItemContext={(item) => item?.description}
                    placeholder="Select template..."
                    emptyMessage="No template found."
                    isItemsLoading={isMatterTemplatesLoading}
                    isItemsError={isMatterTemplatesError}
                    disabled={isRetryMode}
                  />
                  <TextFormField
                    register={form.register("generatedFileName")}
                    label="Generated file name"
                    placeholder="Enter a file name"
                    disabled={isRetryMode}
                  />
                  {isRetryMode ? (
                    <FormField
                      control={form.control}
                      name="fileType"
                      render={({ field }) => (
                        <FormItem>
                          <div className="space-y-2">
                            <div className="text-sm font-medium">
                              Generated file types
                            </div>
                            <FormControl>
                              <Input
                                disabled
                                value={getFileTypeLabel(field.value[0])}
                              />
                            </FormControl>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />
                  ) : (
                    <>
                      <MultiselectFormField
                        register={form.register("fileType")}
                        label="Generated file types"
                        options={fileTypeOptions}
                      />
                      <div className="space-y-2">
                        <div className="flex">
                          <span
                            className="text-muted-foreground invisible text-xs leading-none"
                            aria-hidden="true"
                          >
                            Destination folder
                          </span>
                        </div>
                        <div className="flex min-h-10 flex-wrap items-center gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            onClick={() => {
                              setIsSelectDestinationFolderOpen(true);
                            }}
                            disabled={isSubmitting}
                          >
                            Select destination folder
                          </Button>
                          <Typography className="text-muted-foreground min-w-0 truncate text-sm">
                            {destinationFolderLabel}
                          </Typography>
                        </div>
                      </div>
                    </>
                  )}

                  {!isRetryMode ? (
                    <div className="col-span-full space-y-3 rounded-lg border p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="space-y-1">
                          <Typography className="text-sm font-medium">
                            Matter selection
                          </Typography>
                          {selectedMatterCount ? (
                            <Typography className="text-muted-foreground text-sm">
                              {`Generation will only run for ${selectedMatterCount} selected matter${
                                selectedMatterCount === 1 ? "" : "s"
                              }.`}
                            </Typography>
                          ) : (
                            <div className="flex items-start gap-1.5 text-amber-700">
                              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                              <Typography className="text-sm">
                                No matters selected. Generation will apply to
                                all matters in this mass tort.
                              </Typography>
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setIsMatterPickerOpen(true);
                          }}
                          disabled={isSubmitting}
                        >
                          {selectedMatterCount
                            ? "Edit selected matters"
                            : "Select matters"}
                        </Button>
                      </div>
                      {selectedMatterCount ? (
                        <SelectedMattersAccordion
                          selectedMatters={selectedMatters}
                        />
                      ) : null}
                    </div>
                  ) : null}

                  <div className="col-span-full">
                    <Separator />
                  </div>

                  <Typography className="text-muted-foreground col-span-full text-sm">
                    {templateInputHelperText}
                  </Typography>

                  {templateVariables.length ? (
                    Object.keys(templateVariableSections).map(
                      (sectionKey, index) => {
                        const sectionItems =
                          templateVariableSections[sectionKey] ?? [];
                        const instanceGroups =
                          buildInstanceGroups(sectionItems);

                        return (
                          <div
                            key={sectionKey}
                            className="col-span-full contents"
                          >
                            {index > 0 ? (
                              <div className="col-span-full">
                                <Separator />
                              </div>
                            ) : null}
                            {instanceGroups.map((group) => {
                              const headingLabel = `${getRoleLabelForKey(
                                sectionKey,
                              )}${getGroupHeadingSuffix(group, instanceGroups.length)}`;

                              return (
                                <div
                                  key={`${sectionKey}-${group.key}`}
                                  className="col-span-full contents"
                                >
                                  <div className="col-span-full">
                                    <Typography
                                      variant="heading"
                                      className="text-xl"
                                    >
                                      {headingLabel}
                                    </Typography>
                                  </div>
                                  {group.items.map((item) => {
                                    const fieldName =
                                      `matterTemplateInputs.${item.variable}` as const;
                                    const isCustomSelect =
                                      item.parent === "custom" &&
                                      item.massTortCustomFieldValueType ===
                                        "select" &&
                                      Boolean(item.options?.length);

                                    if (isCustomSelect) {
                                      return (
                                        <div
                                          key={item.variable}
                                          className="space-y-2"
                                        >
                                          <div className="text-sm font-medium">
                                            {item.displayTitle}
                                          </div>
                                          <SimpleSelectFormField
                                            register={form.register(fieldName)}
                                            items={item.options ?? []}
                                            placeholder={`Select ${item.displayTitle}`}
                                            formatItemLabel={(option) => option}
                                            formItemClassName="space-y-0"
                                            clearable
                                          />
                                        </div>
                                      );
                                    }

                                    return (
                                      <FormField
                                        key={item.variable}
                                        control={form.control}
                                        name={fieldName}
                                        render={({ field }) => (
                                          <FormItem>
                                            <div className="space-y-2">
                                              <div className="text-sm font-medium">
                                                {item.displayTitle}
                                              </div>
                                              <FormControl>
                                                <Input
                                                  {...field}
                                                  value={field.value ?? ""}
                                                  placeholder={`Enter ${item.displayTitle}`}
                                                />
                                              </FormControl>
                                              <FormMessage />
                                            </div>
                                          </FormItem>
                                        )}
                                      />
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        );
                      },
                    )
                  ) : (
                    <div className="bg-muted/25 border-border col-span-full rounded-lg border border-dashed p-4">
                      <Typography className="text-muted-foreground text-sm">
                        {selectedTemplate
                          ? "This template does not have variables to configure."
                          : "Select a template to configure optional template inputs."}
                      </Typography>
                    </div>
                  )}

                  {selectedTemplate ? (
                    <div className="col-span-full flex flex-col gap-y-2">
                      <Separator />
                      <FileViewer
                        fileType="matter-template"
                        file={templateFile}
                        viewerClassName="bg-card items-stretch justify-stretch"
                        compactDocxPreview={false}
                      />
                    </div>
                  ) : null}
                </fieldset>
              </div>
            </form>
          </div>
        </AppModal>
        {!isRetryMode ? (
          <>
            <MassTortMatterSelectionModal
              open={isMatterPickerOpen}
              setOpen={setIsMatterPickerOpen}
              massTortId={props.massTortId}
              selectedMatters={selectedMatters}
              setSelectedMatters={setSelectedMatters}
            />
            <SelectDestinationFolderModal
              open={isSelectDestinationFolderOpen}
              setOpen={setIsSelectDestinationFolderOpen}
              massTort={props.massTort}
              initialFolderId={selectedDestinationFolderId}
              onFolderSelected={setSelectedDestinationFolderId}
              title="Destination folder"
              description="Choose where generated mass tort files will be stored."
            />
          </>
        ) : null}
      </>
    </Form>
  );
}
